import type { Map as MapLibreMap } from "maplibre-gl";
import type * as maplibregl from "maplibre-gl";
import type { Feature, Geometry, GeoJsonProperties } from "geojson";
import type { ArcgisLayerConfig } from "@/types/layers";

const INCHES_PER_METER = 39.37;
const DEFAULT_SCREEN_DPI = 96;
const INITIAL_RESOLUTION = 156543.03392804097; // meters/pixel at zoom 0

const calculateScale = (zoom: number, latitude: number): number => {
  const resolution = INITIAL_RESOLUTION / Math.pow(2, zoom);
  const cosLatitude = Math.max(Math.cos((latitude * Math.PI) / 180), 0.00001);
  const adjustedResolution = resolution * cosLatitude;
  return adjustedResolution * DEFAULT_SCREEN_DPI * INCHES_PER_METER;
};

export const identifyDynamicLayer = async (
  map: MapLibreMap,
  config: ArcgisLayerConfig,
  lng: number,
  lat: number,
  scaleRanges?: Record<number, { minScale: number; maxScale: number }>
): Promise<Feature<Geometry, GeoJsonProperties>[]> => {
  // First check if the layer is actually visible at current zoom level
  const layerId = `arcgis-${config.key}-source-layer`;
  const layer = map.getLayer(layerId);

  if (!layer) {
    console.debug(`[Identify] Layer ${layerId} not found`);
    return [];
  }

  // Check if the layer is currently visible (not hidden by zoom constraints)
  const currentZoom = map.getZoom();
  const layerStyle = map.getLayer(layerId) as maplibregl.LayerSpecification;

  // Check minzoom and maxzoom constraints if they exist
  if (layerStyle?.minzoom !== undefined && currentZoom < layerStyle.minzoom) {
    console.debug(
      `[Identify] Layer ${config.key} hidden: zoom ${currentZoom} < minzoom ${layerStyle.minzoom}`
    );
    return [];
  }

  if (layerStyle?.maxzoom !== undefined && currentZoom >= layerStyle.maxzoom) {
    console.debug(
      `[Identify] Layer ${config.key} hidden: zoom ${currentZoom} >= maxzoom ${layerStyle.maxzoom}`
    );
    return [];
  }

  console.debug(
    `[Identify] Attempting identify for ${config.key} at [${lng}, ${lat}]`
  );

  // Build the identify URL with proper parameters
  const bounds = map.getBounds();
  const canvas = map.getCanvas();
  const identifyUrl = `${config.url}/identify`;

  const params = new URLSearchParams({
    f: "json",
    geometry: JSON.stringify({
      x: lng,
      y: lat,
      spatialReference: { wkid: 4326 },
    }),
    geometryType: "esriGeometryPoint",
    sr: "4326",
    mapExtent: `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`,
    imageDisplay: `${Math.round(canvas.width)},${Math.round(canvas.height)},96`,
    tolerance: "8",
    returnGeometry: "true",
    outFields: "*",
  });

  // Add layers parameter if specified
  if (config.layer_numbers && config.layer_numbers.length > 0) {
    params.append("layers", `all:${config.layer_numbers.join(",")}`);
  } else {
    params.append("layers", "all");
  }

  try {
    const response = await fetch(`${identifyUrl}?${params.toString()}`);

    if (!response.ok) {
      console.warn(
        `Identify request failed for ${config.key}: ${response.status}`
      );
      return [];
    }

    const data = await response.json();

    // Check if we actually have results with meaningful data
    if (!data.results || data.results.length === 0) {
      return [];
    }

    const currentScale = calculateScale(map.getZoom(), lat);
    const features: Feature<Geometry, GeoJsonProperties>[] = [];

    // Collect all valid results with actual feature attributes
    for (const result of data.results) {
      if (!result.attributes || Object.keys(result.attributes).length === 0) {
        continue; // Skip empty results
      }

      // Check if we have meaningful attribute values (not just null/empty)
      const hasRealData = Object.values(result.attributes).some(
        (value) =>
          value !== null && value !== undefined && value !== "" && value !== 0
      );

      if (!hasRealData) {
        continue;
      }

      const layerScaleRange = scaleRanges?.[result.layerId];
      if (layerScaleRange) {
        if (
          layerScaleRange.maxScale > 0 &&
          currentScale < layerScaleRange.maxScale
        ) {
          console.debug(
            `[Identify] Layer ${config.key} result ${result.layerId} skipped: scale ${currentScale} < maxScale ${layerScaleRange.maxScale}`
          );
          continue;
        }

        if (
          layerScaleRange.minScale > 0 &&
          currentScale > layerScaleRange.minScale
        ) {
          console.debug(
            `[Identify] Layer ${config.key} result ${result.layerId} skipped: scale ${currentScale} > minScale ${layerScaleRange.minScale}`
          );
          continue;
        }
      }

      // Convert to GeoJSON feature
      let geometry: Geometry;
      if (result.geometry && result.geometryType) {
        // Convert Esri geometry to GeoJSON based on type
        if (result.geometryType === "esriGeometryPoint") {
          geometry = {
            type: "Point",
            coordinates: [result.geometry.x, result.geometry.y],
          };
        } else if (result.geometryType === "esriGeometryPolyline") {
          geometry = {
            type:
              result.geometry.paths?.length === 1
                ? "LineString"
                : "MultiLineString",
            coordinates:
              result.geometry.paths?.length === 1
                ? result.geometry.paths[0]
                : result.geometry.paths,
          };
        } else if (result.geometryType === "esriGeometryPolygon") {
          geometry = {
            type: "Polygon",
            coordinates: result.geometry.rings,
          };
        } else {
          geometry = { type: "Point", coordinates: [lng, lat] };
        }
      } else {
        geometry = { type: "Point", coordinates: [lng, lat] };
      }

      features.push({
        type: "Feature",
        geometry,
        properties: {
          ...result.attributes,
          layerId: result.layerId,
          layerName: result.layerName,
          displayFieldName: result.displayFieldName,
          geometryType: result.geometryType,
        },
      });
    }

    return features;
  } catch (error) {
    console.warn(`Identify failed for ${config.key}:`, error);
    return [];
  }
};
